
import { ElemCard, FilteredCasesDisplay } from './ElemHomeView';

import { hasMathJaxPattern } from '../../utils/Util';

export class ElemPlayView {

    constructor( Dyn, container ) {

        // references to other objects
        this.Dyn = Dyn;
        this.container = container;
        this.bounds = new Dyn.mathUtil.Rect(0,0,0,0); // area for cards in play area
        this.topMargin = { height:0, titleHeight:0, countHeight:0, titleFontSize:0, title:null }
        this.bottomMargin = { height:0, titleFontSize:0, title:null }
        this.bins = []; // array of Dyn.mathUtil.Rect for order-by or group-by
        this.coordMap = null; // Dyn.mathUtil.CoordMap - utility for tracking position across play area
        this.dragInfo = null; // Dyn.AxisLabelUtil
        this.labelInfo = new Dyn.AxisLabelUtil( true );
        this.isHorizontal = true; // for DragCategoryUtil callbacks
        this.filteredCasesDisplay = null;
    }

    destroy() {
        this.Dyn = null;
        this.container = null;
    }

    static playCategoryFontSize = 13;
    static playCountFontSize = 26;
    static nonStackOrderByOffset = 8;

    initVisuals() {
        let Dyn = this.Dyn;

        this.updateTitleHeight();
        this.topMargin.title = this.container.paper.text( 0, 0, "" ).hide();
        this.bottomMargin.height = Dyn.plotUtil.kAxisTitleFontSize * 2;
        //this.bottomMargin.title = this.container.paper.text( 0, 0, "" ).hide();
        this.bottomMargin.title = this.container.paper.text( 0, 0, "" )
            .attr({ 'font-size': Dyn.preferences.getFontSize( Dyn.plotUtil.kAxisTitleFontSize )})
            .hide();

        this.filteredCasesDisplay = new FilteredCasesDisplay( this.Dyn, this.container, "top-right" );
    }

    /** update the space for the top margin, whenever font sizes change or isShowingCount changes */
    updateTitleHeight() {
        let Dyn = this.Dyn;
        this.topMargin.titleFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kPlotTitleFontSize );
        this.topMargin.titleHeight = 2.0 * this.topMargin.titleFontSize;
        this.topMargin.countHeight = 1.0 * Dyn.preferences.getFontSize( ElemPlayView.playCountFontSize );
        this.topMargin.height = this.topMargin.titleHeight +
            (this.container.options.isShowingCount ? this.topMargin.countHeight : 0);

        this.bottomMargin.titleFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kAxisTitleFontSize );
    }

    updateVisuals( noAnimation=true ) {
        let state = this.container.state;
        this.updateTitleHeight();
        this.updateFilterDisplay();
        switch( state?.plot ) {
            case "orderBy":
                this.orderBy( this.container.dataSet, state.attribute, this.container.home.smallCards, noAnimation );
                break;
            case "groupBy":
                this.groupBy( this.container.dataSet, state.attribute, this.container.home.smallCards, noAnimation );
                break;
        }
        // To let React Component know about the options changed
        this.container.onStateChange({options: this.container.options});
    }

    updateFilterDisplay() {
        this.filteredCasesDisplay.update( this.container.dataSet, this.bounds );
    }

    restorePlot( plotType, attribute ) {
        // update our internal state
        this.updateTitleHeight();
        // ask container to make plot (which includes updating its state)
        if( attribute ) switch( plotType ) {
            case "groupBy":
                this.container.groupBy( attribute );
                break;
            case "orderBy":
                this.container.orderBy( attribute );
        }
    }

    resize( newSize ) {
        let Dyn = this.Dyn;
        this.bounds.copyRect( newSize );
        this.bounds.addDebugRect( this.container.paper, "green" );
        Dyn.assertValidRect( this.bounds );
        // Note: x, y ignored if titles are MathJax, use transform instead.
        this.topMargin.title.attr({ x:this.bounds.centerX(), y:this.bounds.top()+this.topMargin.titleHeight/2 });
        this.bottomMargin.title.attr({ x:this.bounds.centerX(), y:this.bounds.bottom()-this.bottomMargin.height/2 });
        if( this.topMargin.title.type === 'g' ) {
            Dyn.assert( this.bottomMargin.title.type === 'g');
        }
    }

    /** arrange small cards in z-order, card backs over outlines */
    layerElements( layerCardBacks ) {
        if( layerCardBacks ) {
            let smallCards = this.container.home.smallCards;
            this.bins.forEach((bin) => bin.layerCards( smallCards, this.Dyn ));
        }
    }

    isCardInPlayArea( card ) {
        // if the card with the given case index is in one of the bins, it is in the play area
        this.Dyn.assert( card && Number.isFinite( card.smallCardIndex ), "invalid card");
        return Boolean( this.bins.find( bin => bin.hasSmallCardIndex( card.smallCardIndex )));
    }

    clearTitles() {
        if( this.topMargin?.title ) {
            this.topMargin.title.hide();
        }
        if( this.bottomMargin?.title ) {
            this.bottomMargin.title.hide();
        }
    }

    resetPlay() {
        this.makeBins( 1 ); // remove any order-by and group-by bins
        this.clearTitles();
    }

    groupBy( dataSet, attr, smallCards, noAnimation ) {
        let Dyn = this.Dyn;
        Dyn.assert( attr?.stats.isCategorical() || attr.stats.isMissing(), "groupBy() is only for non-numeric attributes" );
        this.startOrderOrGroup( dataSet, attr, Dyn.loc("Grouped by {{name}}", {name:attr.name}));
        let sd = this.updateStacksAndBins( attr, smallCards, false, noAnimation );

        // add list of cards for each bin
        smallCards.forEach((card) => {
            let caseValue = attr.cases[ card.caseIndex ],
                catIndex = attr.stats.categories.indexOf( caseValue ); // convert to category index
            if( catIndex >= 0 && catIndex < this.bins.length ) {
                this.bins[catIndex].addCard( card.smallCardIndex, sd );
            } else {
                sd.cardsHavingMissingCases.push( card.smallCardIndex );
            }
        });
        this.updateStackData( sd );
        this.moveSmallCardsToBinsOrHide( smallCards, sd );
        this.updateBinCounts( sd );
        this.endOrderOrGroup( dataSet, attr );
    }

    orderBy( dataSet, attr, smallCards, noAnimation ) {
        let Dyn = this.Dyn;
        Dyn.assert( attr?.statsPreFilter.isNumeric(), "orderBy() is only for numeric attributes" );
        this.startOrderOrGroup( dataSet, attr, Dyn.loc("Ordered by {{name}}", {name:attr.name}) );
        let sd = this.updateStacksAndBins( attr, smallCards, true, noAnimation );

        // add list of cards for each bin
        smallCards.forEach((card) => {
            let caseValue = attr.cases[ card.caseIndex ],
                category = attr.valueToString(caseValue),
                catIndex = attr.stats.categories.indexOf( category ); // convert to category index
            if( catIndex >= 0 && catIndex < this.bins.length ) {
                this.bins[catIndex].addCard( card.smallCardIndex, sd );
            } else {
                sd.cardsHavingMissingCases.push( card.smallCardIndex );
            }
        });
        this.updateStackData( sd );
        this.moveSmallCardsToBinsOrHide( smallCards, sd );
        this.updateBinCounts( sd );
        this.endOrderOrGroup( dataSet, attr );
    }

    startOrderOrGroup( dataSet, attr, title ) {
        attr.updateStatsIfDataChange(); // update categories if filtered
        if( attr.statsPreFilter.isNumeric() ) {
            attr.setOrderedNumeric( true );
        }
        this.updateTitles( attr, title );
    }

    updateTitles( attr, title ) {
        // If the attribute name has MathJax pattern, then render the attribute name as MathJax,
        //  in both the top and bottom margin, else render as text
        let Dyn = this.Dyn;
        const wantMathJaxStr = hasMathJaxPattern(attr.name);

        this.bottomMargin.titleAttrs = {
            text:attr.name,
            x:this.bottomMargin.title.attrs.x,
            y:this.bottomMargin.title.attrs.y,
            'font-size':this.bottomMargin.titleFontSize };
        this.bottomMargin.title = Dyn.axisUtil.updateTextOrMathJax(
            wantMathJaxStr,
            this.bottomMargin.title,
            this.bottomMargin.titleAttrs,
            this.bottomMargin,
            setSvgTitle.bind( this.bottomMargin )).show();

        this.topMargin.titleAttrs = {
            text:title,
            x:this.topMargin.title.attrs.x,
            y:this.topMargin.title.attrs.y,
            'font-size':this.topMargin.titleFontSize };
        this.topMargin.title = Dyn.axisUtil.updateTextOrMathJax(
            wantMathJaxStr,
            this.topMargin.title,
            this.topMargin.titleAttrs,
            this.topMargin,
            setSvgTitle.bind( this.topMargin )).show();

        function setSvgTitle( _svg ) {
            if( ! this.title.node) {
                return;
            }
            this.svgCache = {
                text: this.titleAttrs.text,
                svg: _svg
            }
            // Style svg
            _svg.style.fontSize = this.titleFontSize;
            _svg.style.fontWeight = "bold";
            // Append the svg into the title node
            this.title.node.appendChild(_svg);
            // To position it in center of the give x and y position
            let bbox = this.title.getBBox();
            this.title.attr({
                transform: 'r0T'+ Math.round(this.titleAttrs.x - bbox.width/2) +',' + Math.round(this.titleAttrs.y - bbox.height/2)
            });
        }
    }

    endOrderOrGroup( dataSet, attr ) {
        attr.setOrderedNumeric(false); // remove temporary order statistics
    }

    updateStacksAndBins( attr, smallCards, isOrderBy, noAnimation ) {
        let Dyn = this.Dyn,
            paper = this.container.paper,
            categories = attr.stats.categories,
            numBins = Math.max( 1, attr.stats.numCategories() ),
            oneBlankBin = numBins > attr.stats.numCategories(),
            numDraggableCategories = isOrderBy ? 0 : numBins;

        this.labelInfo = new Dyn.AxisLabelUtil( true );
        this.makeBins( numBins, true, isOrderBy, attr );
        this.dragInfo = new Dyn.DragCategoryUtil( this,
            this.coordMap.transform( 0 ), this.coordMap.signedLength( 1 ), numDraggableCategories, attr );
        let sd = this.makeStackData( attr, smallCards, isOrderBy, noAnimation );

        if( isOrderBy ) {
            // add line from first to last bin (guaranteed to have at least one bin)
            this.bins[0].addOrderByLine( Dyn, paper, this.bins[this.bins.length-1] );
            this.bins.forEach((bin, index) => {
                let categoryName = attr.categoryValueToString( categories[index] );
                //if( index===this.labelInfo.indexOfLongestLabel) console.log("Longest Label:", categoryName, this.labelInfo );
                bin.addBinLabel( Dyn, paper, categoryName, index, this.dragInfo, this.labelInfo );
            });
        } else {
            // add category bin lines and titles
            this.bins.forEach((bin, index) => {
                let categoryName = oneBlankBin ? "" : attr.categoryValueToString( categories[index] );
                bin.addBinLabel( Dyn, paper, categoryName, index, this.dragInfo, this.labelInfo );
                bin.addBinLine( Dyn, paper, index===0, sd );
            });
        }
        return sd;
    }

    // make the bins use by orderBy and groupBy, or clear bins with makeBins( 1 );
    makeBins( numBins, withLabel=false, withLine=false, attr=null ) {

        // reduce the bin bounds to account for cards larger than bin width
        // and top and bottom margin
        let Dyn = this.Dyn;
        let testBinWidth = (this.bounds.width-ElemCard.cardWidth) / numBins,
            marginX = Math.max( 0, (ElemCard.cardWidth - testBinWidth)/2),
            allBinsRect = new Dyn.mathUtil.Rect( this.bounds ).expand( -marginX, 0 );
        allBinsRect.y += this.topMargin.height;
        allBinsRect.height = Math.max( ElemCard.cardHeight, allBinsRect.height - this.topMargin.height - this.bottomMargin.height );

        if( Dyn.debugViewRects && true /* show top and bottom rects if debugging */) {
            this.topMargin.bbox?.removeDebugRect();
            this.topMargin.bbox = (new Dyn.mathUtil.Rect(
                this.bounds.x + 1, this.bounds.y, this.bounds.width - 2, allBinsRect.y - this.bounds.y ))
                .normalize();
            this.topMargin.bbox.addDebugRect( this.container.paper, "orange" );
            this.bottomMargin.bbox?.removeDebugRect();
            this.bottomMargin.bbox = (new Dyn.mathUtil.Rect(
                this.bounds.x + 1, allBinsRect.bottom(), this.bounds.width - 2, this.bounds.bottom() - allBinsRect.bottom() ))
                .normalize();
            this.bottomMargin.bbox.addDebugRect( this.container.paper, "orange" );
        }

        // map bin indices 0-(n-1) to view-left to view-right (in X), like AxisView.makeCategoricalAxis()
        this.coordMap = new Dyn.mathUtil.CoordMap(
            allBinsRect.left(), allBinsRect.right(),
            -0.5, numBins - 0.5 );

        // determine label sizes, rotation, and visibility (hide every X label if too close to each other)
        const kSpaceBetweenLabels = 6; // minimum number of pixels between adjacent labels (otherwise rotate to get more space)
        let labelFontSize = Dyn.preferences.getFontSize( ElemPlayView.playCategoryFontSize ),
            labelHeight = attr? this.labelInfo.getLabelThicknessAndRotation( attr.stats.categories,
                attr, this.container.paper, allBinsRect.width, labelFontSize, kSpaceBetweenLabels ) : 0;
        labelHeight = Math.min( labelHeight, allBinsRect.height * Dyn.plotUtil.kAxisMaxProportion );
        this.labelInfo.showEveryXLabelsStart( this.coordMap.scale );

        // create or reset the bins - an array of empty rectangles
        //      See also PlotShaper.updateBinPositions() for bin-making for dot plots
        if( this.bins.length > numBins ) {
            this.bins.splice( numBins, this.bins.length-numBins )
                .forEach((b) => b.resetCards());
        }
        for( let i = this.bins.length; i < numBins; i++ ) {
            this.bins.push( new PlayBin( Dyn ));   // add empty rectangle
        }

        // update the bin boundary values
        this.bins.forEach((bin, index) => {
            bin.resetCards();
            bin.updateLabel( Dyn, withLabel, labelHeight, this.labelInfo.isVisibleLabel( index ) );
            bin.updateRect( Dyn, index, this.coordMap, allBinsRect, numBins, withLabel, withLine );
            if( Dyn.debugViewRects ) {
                bin.rect.addDebugRect( this.container.paper, "#c99" );
            }
        });
        Dyn.assert( this.bins.length === numBins );
    }

    makeStackData( attr, smallCards, isOrderBy, noAnimation ) {
        // return stack data
        const bin0rect = this.bins[0].rect,
            isIconOnly = Boolean(this.container.options.isIconOnly),
            isSpreadOut = Boolean(this.container.options.isSpreadOut);
        let sd = { // stack data
            cardWidth: (isIconOnly ? ElemCard.cardIconSize : ElemCard.cardWidth ),
            cardHeight: (isIconOnly ? ElemCard.cardIconSize : ElemCard.cardHeight ),
            binWidth: bin0rect.width,
            binHeight: bin0rect.height,
            maxStack: 0, // # cards in the tallest stack, as updated by PlayBin.addCard()
            multiColumnGroupByStack: Boolean((!isOrderBy) && (isSpreadOut)),
            constrainedOrderByStack: Boolean( isOrderBy && (!isSpreadOut)),
            useOverallCount: isOrderBy,
            cardsHavingMissingCases: [],
            isIconOnly: isIconOnly,
            animateData: {
                speed: noAnimation ? this.container.noAnimation() : this.container.slowAnimation(),
                selectedAttribute: attr,
                Dyn: this.Dyn,
            }
        };
        sd.stackBase = bin0rect.bottom() - (isIconOnly ? ElemCard.cardIconBottom : ElemCard.cardHeight);
        sd.stackHeight = Math.max(1, sd.stackBase - (bin0rect.top()));
        sd.jitterInX = Boolean((sd.cardWidth < sd.binWidth) && ! isOrderBy);
        sd.jitterInY = Boolean( sd.cardHeight*1.5 < sd.binHeight && ! ( sd.jitterInX || isIconOnly ));

        return sd;
    }

    updateStackData( stackData ) {
        let Dyn = this.Dyn,
            sd = stackData;

        if( sd.multiColumnGroupByStack ) {
            // for multi-column stacks, first calc stack columns assuming no overlap
            let maxStackHeight = Math.floor( Math.max( 1, (sd.stackHeight / sd.cardHeight) ) );
            let maxStackWidth = Math.max( 1, sd.binWidth - 2*PlayBin.stackMargin );
            let maxStackColumns = Math.max( 1, Dyn.mathUtil.floor( maxStackWidth / sd.cardWidth ) );
            sd.numColumns = Dyn.mathUtil.clipToMinMax( 1, maxStackColumns,
                Dyn.mathUtil.ceiling( sd.maxStack / maxStackHeight ) );
            sd.stackOffset = sd.cardHeight;
            // then shrink space between stacks if needed to fit all cards/icons with the given number of columns
            let numIconsPerColumn = Dyn.mathUtil.floor( sd.maxStack / sd.numColumns );
            let maxPixelsPerIcon = sd.stackHeight / numIconsPerColumn;
            sd.stackOffset = Math.min( maxPixelsPerIcon, sd.stackOffset );
        } else {
            // single column for stack
            sd.numColumns = 1;
            sd.stackOffset = Math.max( 1, Math.min( sd.cardHeight,
                Math.floor( sd.stackHeight / (sd.maxStack - 1) ) ) );
            if( sd.constrainedOrderByStack ) { // for orderBy, limit stack offset to a few pixels at most
                sd.stackOffset = Math.min( sd.stackOffset, ElemPlayView.nonStackOrderByOffset );
            }
        }
        //console.log( sd );
    }

    moveSmallCardsToBinsOrHide( smallCards, sd ) {

        // move cards into stack
        this.bins.forEach((bin) => {
            bin.stackCards( smallCards, sd );
        });

        // hide cards with missing cases, if any
        sd.cardsHavingMissingCases.forEach((smallCardIndex) => {
            PlayBin.setCardMissingAndHide( smallCards[smallCardIndex], sd.isIconOnly, this.Dyn );
        });
    }

    /** show or hide the bin counts */
    updateBinCounts( sd ) {
        let showCounts = this.container.options.isShowingCount,
            overallCount = this.bins.reduce( (accumulator, bin) => accumulator + bin.numCardsInBin(), 0 ),
            lastBinIndex = this.bins.length - 1;
        if( sd.useOverallCount ) {
            this.bins.forEach( ( bin, i ) => {
                bin.addCount( this.Dyn, this.container.paper, overallCount, showCounts && i===lastBinIndex, true );
            } );
        } else {
            this.bins.forEach( ( bin ) => {
                bin.addCount( this.Dyn, this.container.paper, bin.numCardsInBin(), showCounts );
            } );
        }
    }

    /** move the given attribute category to the new position,
     * and move the other categories in the legend out of the way.
     * @param v -- new position in x or y of dragged category
     * @param oldIndex -- original (pre-drag) index of dragged category (in list of the attribute's categories)
     * @param newIndex -- new index of dragged category
     * @param dragInfo {Dyn.DragCategoryUtil} -- drag information (shared by all categories)
     * @param finalAnimation {boolean} -- true if we want dragged category to animate to new position
     */
    moveCategoriesToDragPosition( v, oldIndex, newIndex, dragInfo, finalAnimation ) {
        this.bins.forEach( (bin, binIndex ) => {
            let isDragging = ( binIndex === oldIndex && ! finalAnimation ),
                newBinIndex = dragInfo.getCategoryIndexWithSkipping( binIndex, oldIndex, newIndex ),
                dragX = dragInfo.getLimitedDragPosition( v ),
                binX = dragInfo.cat0Coord+(dragInfo.catOffset*newBinIndex);
            bin.moveToDragPosition( isDragging, dragX, binX, newBinIndex, this.container.home.smallCards, dragInfo, this.labelInfo );
        });
    }

    removeCard( smallCard ) {
        console.log("ElemPlayView.removeCard", smallCard.smallCardIndex );
        this.bins.forEach((bin) => {
            bin.smallCardIndices = bin.smallCardIndices.filter(i => (i !== smallCard.smallCardIndex) );
        });
    }

}

class PlayBin {
    constructor( Dyn ) {
        this.Dyn = Dyn;
        this.rect = new Dyn.mathUtil.Rect( 0, 0, 0, 0 );
        this.smallCardIndices = [];   // array of small card indices, for cards in bin
        this.label = null; // paper.text() for bin label (on bottom of bin)
        this.tick = null; // paper.circle() for bin tick mark (on bottom of bin)
        this.line = null;  // paper.path() for bin dividing line (on one side of bin)
        this.count = null; // paper.text() for bin count (on top of bin)
        this.labelHeight = 0;
        this.labelVisible = false;
        this.orderByLineHeight = 0;
        this.jitterInX = false;
    }

    //static minBinHeight = 20;
    static stackMargin = 10; // space between stacked cards and left/right edge of bin
    static labelTipOffsetX = 0;
    static labelTipOffsetY = 30;
    static tickColor = '#888';

    updateLabel( Dyn, useBinLabel, labelHeight, isVisibleLabel ) {
        const spacer = 1*Dyn.preferences.getFontSize( ElemPlayView.playCategoryFontSize );
        this.labelHeight = useBinLabel ? labelHeight+spacer : 0;
        this.labelVisible = isVisibleLabel;
    }

    // make bins, similar to AxisView.updateRectToSeparation1D()
    updateRect( Dyn, index, coordMap, overallBounds, numBins, useBinLabel, useOrderByLine ) {
        const lineHeight = 2* Dyn.preferences.getFontSize( ElemPlayView.playCategoryFontSize );
        this.orderByLineHeight = useOrderByLine ? lineHeight : 0;
        let bottomMargin = this.labelHeight + this.orderByLineHeight;
        this.rect.copyRect( overallBounds ); // default bin covers entire plot interior
        this.rect.height = Math.max( ElemCard.cardHeight, this.rect.height - bottomMargin );
        if( numBins > 1 ) {
            this.rect.x = coordMap.transform( index - 0.5 );
            this.rect.width = coordMap.scaleAbs( 1 );
        }
    }

    // remove label and record of smallCards in this bin
    resetCards() {
        this.rect.removeDebugRect();
        this.smallCardIndices = [];
        this.label?.remove();
        this.label = null;
        this.tick?.remove();
        this.tick = null;
        this.line?.remove();
        this.line = null;
        this.count?.remove();
        this.count = null;
    }

    // add a small card to this bin, and update maximum number of cards in all bins (maxStack)
    addCard( smallCardIndex, stackData ) {
        let sd = stackData;
        this.smallCardIndices.push( smallCardIndex );
        sd.maxStack = Math.max( sd.maxStack, this.numCardsInBin() );
    }

    /** position the small cards in this bin, with various stacking options. See also setCardMissingAndHide() */
    stackCards( smallCards, stackData ) {
        const sd = stackData,
            anim = sd.animateData,
            maxCasesOrColumns = Math.min( this.numCardsInBin(), sd.numColumns ),
            offsetCardToIcon = 0.5 * (ElemCard.cardWidth - sd.cardWidth), // nonzero if isIconOnly
            columnOffset = this.rect.centerX() - (0.5 * (maxCasesOrColumns * sd.cardWidth)) - offsetCardToIcon,
            jitterInYAmount = Math.random(); // Y-jitter: all smallCards in a single stack move together
        this.jitterInX = sd.jitterInX; // remember for getBinConstraintForCard()
        this.smallCardIndices.forEach((smallCardIndex, stackIndex) => {
            let col = stackIndex % sd.numColumns,
                row = Math.floor( stackIndex / sd.numColumns ),
                x = columnOffset + col*sd.cardWidth
                    + (sd.jitterInX ? (sd.cardWidth*0.2)*(Math.random() - 0.5) : 0),
                y = sd.stackBase - row*sd.stackOffset
                    - (sd.jitterInY ? (sd.cardHeight*0.2)*jitterInYAmount : 0),
                card = smallCards[smallCardIndex],
                color = ElemCard.getCaseColor( anim.Dyn, anim.selectedAttribute, card.caseIndex );
            card.setIsMissing( false );
            card.setIsHome( false );
            card.setIsDragged( false );
            card.showHideCard( stackData.isIconOnly, this.Dyn );
            card.back.toFront();
            card.animateCardTo( "t"+x+","+y, anim.speed, color );
            card.back.getRRect().data( "offsetX", x-this.rect.centerX() );
        });
    }

    /** hide cards not in any stack, because they have missing values and are not plottable. */
    static setCardMissingAndHide( card, isIconOnly, Dyn ) {
        card.setIsMissing( true );
        card.setIsHome( false );
        card.setIsDragged( false );
        card.showHideCard( isIconOnly, Dyn );
    }

    layerCards( smallCards, Dyn ) {
        this.label?.toFront();
        this.line?.toFront();
        this.smallCardIndices.forEach( smallCardIndex => {
            let card = smallCards[smallCardIndex];
            if( !card ) console.log( "PlayBin.LayerCards() out of date, smallCardIndex", smallCardIndex, "does not exist");
            card?.back.toFront();
        });
        this.count?.toFront();
    }

    getLabelTextTransform( Dyn, labelInfo ) {
        return labelInfo.wantRotation // compare to AxisView.getLabelTextTransform()
            ? Dyn.plotUtil.getTextTransform( this.label, "r-90,center,top" ) // vertical text: anchor to center-top
            : Dyn.plotUtil.getTextTransform( this.label, "center,top" ); // horizontal text: anchor to center-top
    }

    // add or update the label in this bin
    addBinLabel( Dyn, paper, labelString, categoryIndex, dragInfo, labelInfo ) {
        const tickWidth = 0.8,
            tickLength = 2.5,
            tickOffset = 3,
            labelOffset = tickOffset+tickLength;
        let text = labelString || "", // handle empty label
            fontSize = Dyn.preferences.getFontSize( ElemPlayView.playCategoryFontSize ),
            x = this.rect.centerX(),
            y = this.rect.bottom() ;//+ this.labelHeight/2;
        if( ! this.tick ) {
            this.tick = paper.rect( x, y, tickWidth, tickLength );
        }
        this.tick.attr( {
            x: x-tickWidth/2, cy: y + tickOffset,
            stroke: 'none', fill: PlayBin.tickColor } )
            .data( "tooltip", labelString )
            .hover( hoverOverLabel, hoverOutLabel )
            .show();
        if( ! this.label ) {
            this.label = paper.text( x, y+labelOffset, text );
        }
        if( this.labelVisible ) {
            this.label
                .attr( {
                    x: x, y: y + labelOffset, text: text,
                    "font-size": fontSize,
                    fill: Dyn.plotUtil.titleColor,
                    class: 'tde-attribute-name ' + Dyn.plotUtil.hideOnPreviewClass
                } )
                .data( "tooltip", labelString )
                .data( "category", labelString )
                .data( "dragInfo", dragInfo )
                .data( "catIndex", categoryIndex )
                .hover( hoverOverLabel, hoverOutLabel )
                .drag( dragMoveCategory, startDragCategory, endDragCategory );
            if( labelInfo.wantRotation ) {
                Dyn.plotUtil.clipStringToWidth( this.label, this.labelHeight - labelOffset );
            }
            this.label.attr( "transform", this.getLabelTextTransform( Dyn, labelInfo ) );
            this.label.show();
        } else {
            this.label.hide();
        }

        function showToolTip( event ) {
            Dyn.toolTip.show( this, event, { x:this.attrs.x+PlayBin.labelTipOffsetX, y:this.attrs.y+PlayBin.labelTipOffsetY });
        }

        function hoverOverLabel( event ) { // similar to AxisView:hoverOverLabel()
            // send xy position of the element to display tool tip accordingly
            var isCategory = Boolean(this.data("category"));
            showToolTip.call( this, event );
            if( isCategory ) {
                event.currentTarget.style.cursor = Dyn.plotUtil.cursorPointer;
            }
        }

        function hoverOutLabel() { // similar to AxisView:hoverOutLabel()
            if( Dyn.toolTip.isMyElement( this ))
                Dyn.toolTip.hide();
        }

        function startDragCategory( x, y, event ) {
            let dragInfo = this.data('dragInfo');
            if( dragInfo.isDraggable ) { // e.g. 2+ categories, not numeric intervals
                this.data('inDrag', true );
                this.data('v0', this.attr( dragInfo.coordName(this)));
                // change cursor while mouse down (then revert to pointer cursor)
                this.attr('cursor', dragInfo.isOnX ? Dyn.plotUtil.cursorHorizontalBothSide : Dyn.plotUtil.cursorVerticalBothSide );
            }
            if( Dyn.environment.isTouchDevice() )
                showToolTip.call( this, event );
            Dyn.plotUtil.safeStopPropogation( event );
        }

        function dragMoveCategory( dx, dy, x, y, event )
        {
            if( this.data( 'inDrag' ) ) {
                let dragInfo = this.data( 'dragInfo' ),
                    deltaCoord = dragInfo.isOnX ? dx : dy,
                    newCoord = this.data( 'v0' ) + deltaCoord,
                    catIndex = this.data( 'catIndex' ),
                    newIndex = dragInfo.getCategoryIndexFromDelta( catIndex, deltaCoord );
                this.data( 'newIndex', newIndex ); // save for dragEndCategory()
                dragInfo.view.moveCategoriesToDragPosition( newCoord, catIndex, newIndex, dragInfo );
                // TODO: set double-arrow cursor while mouse down, even if mouse not over the category label
                //event.currentTarget.style.cursor = dragInfo.isOnX ? Dyn.plotUtil.cursorHorizontalBothSide : Dyn.plotUtil.cursorVerticalBothSide;
            }
            if( Dyn.environment.isTouchDevice() )
                showToolTip.call( this, event );
            Dyn.plotUtil.safeStopPropogation( event );
        }
        function endDragCategory( event ) {
            if( this.data( 'inDrag' ) ) { // this = circle or text element from makeCategoricalAxis()
                let dragInfo = this.data( 'dragInfo' ),
                    oldCoord = this.data( 'v0' ),
                    catIndex = this.data( 'catIndex' ),
                    newIndex = this.data( 'newIndex' );
                if( newIndex >= 0 && newIndex !== catIndex ) {
                    // animate to final position
                    dragInfo.view.moveCategoriesToDragPosition( oldCoord, catIndex, newIndex, dragInfo, true );
                    // save changes and update all views
                    setTimeout( () => {
                        Dyn.mediator.publish( "moveCategoryOfAttribute", {
                            attributeID: dragInfo.attributeID,
                            oldIndex: catIndex,
                            newIndex: newIndex
                        } )
                    }, dragInfo.animationTimeMS ); // give time for animation to finish before updating views
                } else {
                    // drag ended in about the same position, redraw to original pos. to clean up view
                    dragInfo.view.moveCategoriesToDragPosition( oldCoord, catIndex, catIndex, dragInfo, true );
                }
                this.data( 'inDrag', false );
                this.attr( 'cursor', Dyn.plotUtil.cursorPointer );
            }
            if( Dyn.environment.isTouchDevice() )
                hoverOutLabel.call( this, event );
            Dyn.plotUtil.safeStopPropogation( event );
        }
    }

    addBinLine( Dyn, paper, isFirst, stackData) {
        if( ! isFirst ) {
            let pathString = Dyn.plotUtil.makeStraightLinePath( this.rect.x, this.rect.y,
                            this.rect.x, this.rect.bottom() );
            if( ! this.line ) {
                this.line = paper.path( pathString );
            }
            this.line.attr({
                path: pathString,
                stroke: "#ccc"
            });
        }
    }

    /** show or hide the count of smallCards in this bin */
    addCount( Dyn, paper, count, wantCount, rightJustify ) {
        if( wantCount ) {
            let text = count.toString(),
                x = rightJustify ? this.rect.right() : this.rect.centerX(),
                y = this.rect.top();
            if( !this.count ) {
                this.count = paper.text( x, y, text );
            }
            this.count.attr( {
                x: x, y: y, text: text,
                "font-size": Dyn.preferences.getFontSize( ElemPlayView.playCountFontSize )
            } );
            if( rightJustify ) {
                this.count.attr( { "transform": Dyn.plotUtil.getTextTransform( this.count, "right,bottom" ) } );
            } else {
                this.count.attr( { "transform": Dyn.plotUtil.getTextTransform( this.count, "center,bottom" ) } );
                Dyn.plotUtil.clipStringToWidth( this.count, this.rect.width );
            }
            Dyn.plotUtil.disableMouseOverEvents( this.count );
        } else {
            this.count?.hide();
        }
    }

    addOrderByLine( Dyn, paper, lastBin ) {
        let lineY = this.rect.bottom() + this.labelHeight + this.orderByLineHeight/2,
            pathString = this.makeArrowPath( Dyn,
                this.rect.centerX(),    lineY,
                lastBin.rect.centerX(), lineY,
                Dyn.plotUtil.arrowAnnotationLength, Dyn.plotUtil.arrowAnnotationWidth );
        if( ! this.line ) {
            this.line = paper.path( pathString );
        }
        this.line.attr({
            path: pathString,
            stroke: "blue",
            "stroke-width": 2,
            "stroke-linecap": "round"
        });
    }

    makeArrowPath( Dyn, startX, startY, endX, endY, arrowLength, arrowWidth  ) {
        var x1 = ( startX ).toFixed(1),
            y1 = ( startY ).toFixed(1),
            x2 = ( endX ).toFixed(1),
            y2 = ( endY ).toFixed(1),
            aLine = Dyn.mathUtil.angle( endX - startX, endY - startY ),
            aLeft = Dyn.mathUtil.angle( -arrowLength, arrowWidth/2 ),
            aRight = Dyn.mathUtil.angle( -arrowLength, -arrowWidth/2 ),
            xLeft = ( endX + Dyn.mathUtil.polarToX( arrowLength, aLine + aLeft )).toFixed(1),
            yLeft = ( endY + Dyn.mathUtil.polarToY( arrowLength, aLine + aLeft )).toFixed(1),
            xRight = ( endX + Dyn.mathUtil.polarToX( arrowLength, aLine + aRight )).toFixed(1),
            yRight = ( endY + Dyn.mathUtil.polarToY( arrowLength, aLine + aRight )).toFixed(1);
        /* pathString */
        return "M" + x1 + "," + y1 + // line from start
                "L"+ x2 +","+ y2+ // to end
                "L"+ xLeft +","+ yLeft+ // to left (counter-clockwise arrow point)
                "M"+ x2 +","+ y2+ // to end
                "L"+ xRight +","+ yRight; // to right (clockwise arrow point)
    }

    /**
     * If the given card is outside the bin, return a new location for the card that is in the bin, else return null.
     * @param cardBBox
     * @param cardIndex
     * @returns {null|{x: number, y: number}}
     */
    getBinConstraintForCard( cardBBox /*, caseIndex */ ) {
        const binMargin = cardBBox.width * 0.2;
        let x = cardBBox.x,
            y = cardBBox.y;
        if( cardBBox.width > this.rect.width || !this.jitterInX ) {
            let cardInBinLeft = this.rect.centerX()-cardBBox.width/2,
                cardInBinRight = this.rect.centerX()+cardBBox.width/2;
            if( cardBBox.left() < cardInBinLeft ) {
                x = cardInBinLeft;
            } else if( cardBBox.right() > cardInBinRight ) {
                x = cardInBinRight - cardBBox.width;
            }
        } else {
            if( cardBBox.left() < this.rect.left() ) {
                x = this.rect.left() + binMargin;
            } else if( cardBBox.right() > this.rect.right() ) {
                x = this.rect.right() - cardBBox.width - binMargin;
            }
        }
        if( cardBBox.top() < this.rect.top() ) {
            y = this.rect.top() + binMargin;
        } else if( cardBBox.bottom() > this.rect.bottom() ) {
            y = this.rect.bottom() - cardBBox.height;
        }
        if( ( x !== cardBBox.x || y !== cardBBox.y )) {
            return { x:x, y:y } // return new constrained location
        }
        return null; // no need for constraint
    }

    /** move all the visible elements in this bin to the drag position */
    moveToDragPosition( isDragging, dragX, binX, newIndex, smallCards, dragInfo, labelInfo ) {
        let labelTransform = this.label.attr('transform');
        if( isDragging ) {
            let binOffset = dragX - this.rect.centerX();
            this.rect.x += binOffset;
            this.tick.attr( 'x', dragX );
            unRotateLabel( this ); // because of bugs in positioning rotated text must unrotate first, and not animate
            this.label.attr( 'x', dragX );
            rotateLabel( this );
            this.count?.attr( 'x', dragX );
            this.smallCardIndices.forEach( smallCardIndex => {
                let card = smallCards[smallCardIndex],
                    bbox = card.back.getBBox();
                card.moveCardTo( "t"+(bbox.x+binOffset)+","+(bbox.y));
            });
        } else {
            // reposition other categories out of the way (leave a blank line where dragged category will go)
            let binOffset = binX - this.rect.centerX();
            this.rect.x += binOffset;
            if( labelInfo.wantRotation ) {
                unRotateLabel( this ); // because of bugs in positioning rotated text must unrotate first, and not animate
                this.tick.attr( 'x', binX );
                this.label.attr( 'x', binX );
                rotateLabel( this );
            } else {
                this.tick.animate( { 'x': binX }, dragInfo.animationTimeMS, dragInfo.animationEasing );
                this.label.animate( { 'x': binX }, dragInfo.animationTimeMS, dragInfo.animationEasing );
            }
            this.count?.animate( { 'x': binX }, dragInfo.animationTimeMS, dragInfo.animationEasing );
            this.smallCardIndices.forEach( smallCardIndex => {
                let card = smallCards[smallCardIndex],
                    offsetX = card.back.getRRect().data("offsetX"),
                    bbox = card.back.getBBox();
                card.animateCardTo( "t"+(binX+offsetX)+","+(bbox.y),
                    { time:dragInfo.animationTimeMS, easing:dragInfo.animationEasing });
            });
        }
        function unRotateLabel( bin ) {
            if( labelInfo.wantRotation )
                bin.label.attr('transform','');
        }
        function rotateLabel( bin ) {
            if( labelInfo.wantRotation )
                bin.label.attr('transform',labelTransform);
        }
    }

    // hasCaseIndex( caseIndex, smallCards ) {
    //     return this.smallCardIndices.find( smallCardIndex => {
    //         this.Dyn.assertArrayIndex( smallCards, smallCardIndex );
    //         return (smallCards[smallCardIndex].caseIndex === caseIndex);
    //     } );
    // }

    hasSmallCardIndex( smallCardIndex ) {
        this.Dyn.assertFiniteNumeric( smallCardIndex );
        return this.smallCardIndices.includes( smallCardIndex );
    }

    numCardsInBin() {
        return this.smallCardIndices.length;
    }
}

